import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import {
  ShoppingCart, CheckCircle, Circle, Receipt, Camera, CircleNotch, Storefront, CaretDown, CaretUp
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const WorkerShoppingLists = () => {
  const [lists, setLists] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [pricePrompt, setPricePrompt] = useState(null); // {listId, itemId, value}
  const [uploading, setUploading] = useState(null);
  const [completing, setCompleting] = useState(null);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  const fetchLists = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopping-lists`, authHeader());
      setLists(res.data?.lists || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const active = lists.filter((l) => l.status !== 'completed');
  if (active.length === 0) return null;

  const toggle = async (listId, item) => {
    if (!item.picked) {
      setPricePrompt({ listId, itemId: item.id, value: '' });
      return;
    }
    try {
      await axios.post(`${API}/shopping-lists/${listId}/items/${item.id}/toggle`, { picked: false }, authHeader());
      fetchLists();
    } catch { /* silent */ }
  };

  const confirmPick = async () => {
    const { listId, itemId, value } = pricePrompt;
    try {
      await axios.post(`${API}/shopping-lists/${listId}/items/${itemId}/toggle`, {
        picked: true,
        actual_price: value === '' ? null : Number(value),
      }, authHeader());
      setPricePrompt(null);
      fetchLists();
    } catch (e) {
      toast({ title: 'Could not update item', description: e.response?.data?.detail, variant: 'destructive' });
    }
  };

  const pickReceipt = (listId) => {
    uploadTarget.current = listId;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const listId = uploadTarget.current;
    if (!file || !listId) return;
    setUploading(listId);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/shopping-lists/${listId}/receipt`, form, authHeader());
      toast({ title: 'Receipt attached' });
      fetchLists();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const complete = async (listId) => {
    setCompleting(listId);
    try {
      await axios.post(`${API}/shopping-lists/${listId}/complete`, {}, authHeader());
      toast({ title: 'Shopping run completed', description: 'The contractor has been notified.' });
      fetchLists();
    } catch (e) {
      toast({ title: 'Could not complete', description: e.response?.data?.detail, variant: 'destructive' });
    } finally {
      setCompleting(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="worker-shopping-lists">
      <h2 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
        <ShoppingCart size={20} weight="fill" className="text-[#10B981]" /> Shopping Runs
        <span className="bg-[#10B981]/10 text-[#10B981] text-[10px] font-black px-2 py-0.5 rounded-full">{active.length}</span>
      </h2>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="worker-receipt-file-input" />

      {active.map((l) => {
        const picked = l.items.filter((i) => i.picked).length;
        const open = expanded === l.id || active.length === 1;
        return (
          <div key={l.id} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-2xl shadow-sm overflow-hidden" data-testid={`worker-shopping-card-${l.id}`}>
            <button onClick={() => setExpanded(open && active.length > 1 ? null : l.id)} className="w-full flex items-center gap-3 p-4 text-left">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900 dark:text-white truncate">{l.title}</p>
                <p className="text-xs text-neutral-500 mt-0.5 truncate">
                  From {l.contractor_name}{l.store ? <> · <Storefront size={11} className="inline -mt-0.5" /> {l.store}</> : ''} · {picked}/{l.items.length} picked
                </p>
              </div>
              <div className="w-11 h-11 rounded-full bg-[#10B981]/10 flex items-center justify-center text-[#10B981] font-bold text-xs shrink-0">
                {Math.round((picked / l.items.length) * 100)}%
              </div>
              {active.length > 1 && (open ? <CaretUp size={14} className="text-neutral-400" /> : <CaretDown size={14} className="text-neutral-400" />)}
            </button>
            {open && (
              <div className="px-4 pb-4 space-y-1">
                {l.notes && <p className="text-xs text-neutral-500 bg-neutral-50 dark:bg-neutral-800 rounded-lg px-3 py-2 mb-2">{l.notes}</p>}
                {l.items.map((i) => (
                  <button
                    key={i.id}
                    onClick={() => toggle(l.id, i)}
                    className="w-full flex items-center gap-3 py-2.5 px-2 rounded-xl active:bg-neutral-50 dark:active:bg-neutral-800 text-left"
                    data-testid={`worker-shopping-item-${i.id}`}
                  >
                    {i.picked
                      ? <CheckCircle size={22} weight="fill" className="text-[#10B981] shrink-0" />
                      : <Circle size={22} className="text-neutral-300 shrink-0" />}
                    <span className={`flex-1 text-sm ${i.picked ? 'text-neutral-400 line-through' : 'text-neutral-800 dark:text-neutral-200'}`}>
                      <span className="font-bold">{i.quantity} {i.unit}</span> — {i.name}
                      {i.notes && <span className="block text-xs text-neutral-400">{i.notes}</span>}
                    </span>
                    {i.actual_price != null && <span className="text-xs font-bold text-[#10B981] shrink-0">${i.actual_price}</span>}
                  </button>
                ))}

                <div className="flex gap-2 pt-3">
                  <button
                    onClick={() => pickReceipt(l.id)}
                    disabled={uploading === l.id}
                    className="flex-1 h-12 flex items-center justify-center gap-2 bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 font-bold text-sm rounded-full active:scale-[0.98] transition-all"
                    data-testid={`worker-shopping-receipt-btn-${l.id}`}
                  >
                    {uploading === l.id ? <CircleNotch size={16} className="animate-spin" /> : <Camera size={16} weight="bold" />}
                    {l.receipt_ids?.length ? `Receipt ✓ (${l.receipt_ids.length})` : 'Add Receipt'}
                  </button>
                  <button
                    onClick={() => complete(l.id)}
                    disabled={completing === l.id || picked === 0}
                    className="flex-1 h-12 flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white font-bold text-sm rounded-full active:scale-[0.98] transition-all disabled:opacity-40"
                    data-testid={`worker-shopping-complete-btn-${l.id}`}
                  >
                    {completing === l.id ? <CircleNotch size={16} className="animate-spin" /> : <Receipt size={16} weight="bold" />}
                    Done Shopping
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {pricePrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="worker-price-modal">
          <div className="bg-white dark:bg-neutral-900 rounded-3xl p-5 w-full max-w-xs">
            <p className="font-bold text-neutral-900 dark:text-white mb-1">Got it! Price paid?</p>
            <p className="text-xs text-neutral-500 mb-3">Per unit — leave blank to skip.</p>
            <input
              type="number" min="0" step="0.01" autoFocus
              value={pricePrompt.value}
              onChange={(e) => setPricePrompt((p) => ({ ...p, value: e.target.value }))}
              placeholder="$ 0.00"
              className="w-full h-12 px-3 border border-neutral-300 dark:border-neutral-600 rounded-xl text-center text-lg font-bold bg-white dark:bg-neutral-800 dark:text-white focus:outline-none focus:border-[#10B981] mb-3"
              data-testid="worker-price-input"
            />
            <div className="flex gap-2">
              <button onClick={() => setPricePrompt(null)} className="flex-1 h-11 bg-neutral-100 dark:bg-neutral-800 font-bold text-sm rounded-full dark:text-white">Cancel</button>
              <button onClick={confirmPick} className="flex-1 h-11 bg-[#10B981] text-white font-bold text-sm rounded-full" data-testid="worker-price-confirm-btn">Check Off</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerShoppingLists;
